/* Created by Interactive 63 - https://interactive63.com */
/**
 * Bounding boxes of path data and the transform that fits one shape onto another, used when
 * bringing a shape in from another drawing.
 */
import svgpath from 'svgpath';
import { toCubicSubpaths } from './normalise';

export interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** The box around a path's points, control points included, or null if it cannot be read. */
export function pathBounds(d: string): Box | null {
  const parsed = toCubicSubpaths(d);
  if (!parsed.ok) return null;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  /** Grows the box to include a point. */
  const include = (x: number, y: number): void => {
    x0 = Math.min(x0, x);
    x1 = Math.max(x1, x);
    y0 = Math.min(y0, y);
    y1 = Math.max(y1, y);
  };
  for (const path of parsed.subpaths) {
    include(path.start[0], path.start[1]);
    for (const seg of path.segs) {
      include(seg[0], seg[1]);
      include(seg[2], seg[3]);
      include(seg[4], seg[5]);
    }
  }
  return { x0, y0, x1, y1 };
}

/**
 * Scales and moves a path so its box matches the target's, keeping its proportions and centring
 * it. Returns the path unchanged if either box cannot be measured.
 */
export function fitPathTo(d: string, targetD: string): string {
  const from = pathBounds(d);
  const to = pathBounds(targetD);
  if (!from || !to) return d;
  const fw = from.x1 - from.x0;
  const fh = from.y1 - from.y0;
  const tw = to.x1 - to.x0;
  const th = to.y1 - to.y0;
  if (fw <= 0 || fh <= 0 || tw <= 0 || th <= 0) return d;
  const scale = Math.min(tw / fw, th / fh);
  const dx = (to.x0 + to.x1) / 2 - ((from.x0 + from.x1) / 2) * scale;
  const dy = (to.y0 + to.y1) / 2 - ((from.y0 + from.y1) / 2) * scale;
  return svgpath(d).scale(scale).translate(dx, dy).round(3).toString();
}

/** Applies a 2D matrix [a, b, c, d, e, f] to path data. */
export function transformPath(d: string, matrix: readonly number[]): string {
  return svgpath(d)
    .matrix([...matrix])
    .round(3)
    .toString();
}
